import axios from 'axios';
import { getToken } from './auth';
import { DashboardStats } from '../models';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include token
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

interface PaginationParams {
  page?: number;
  page_size?: number;
  search?: string;
  format?: string;
  status?: string;
  series?: number;
  match_id?: number;
}

// Player related API calls
export const playerApi = {
  getAll: (params?: PaginationParams) => api.get('/cricket/players/', { params }),
  getById: (id: number) => api.get(`/cricket/players/${id}/`),
  create: (data: any) => api.post('/cricket/players/', data),
  update: (id: number, data: any) => api.put(`/cricket/players/${id}/`, data),
  patch: (id: number, data: any) => api.patch(`/cricket/players/${id}/`, data),
  delete: (id: number) => api.delete(`/cricket/players/${id}/`),
  refreshStats: (id: number) => api.get(`/cricket/players/${id}/refresh_stats/`),
};

// Squad related API calls
export const squadApi = {
  getAll: (params?: PaginationParams) => api.get('/cricket/squads/', { params }),
  getByMatch: (matchId: number) => api.get(`/cricket/squads/by_match/`, { params: { match_id: matchId } }),
  getById: (id: number) => api.get(`/cricket/squads/${id}/`),
};

interface MatchFilterParams extends PaginationParams {
  from_date?: string;
  to_date?: string;
  start_time_after?: string;
  start_time_before?: string;
}

// Match related API calls
export const matchApi = {
  getAll: (params?: MatchFilterParams) => api.get('/cricket/matches/', { params }),
  getById: (id: number) => api.get(`/cricket/matches/${id}/`),
  create: (data: any) => api.post('/cricket/matches/', data),
  update: (id: number, data: any) => api.put(`/cricket/matches/${id}/`, data),
  patch: (id: number, data: any) => api.patch(`/cricket/matches/${id}/`, data),
  delete: (id: number) => api.delete(`/cricket/matches/${id}/`),
  getContentBlocksSchema: () => api.get('/cricket/matches/content_blocks_schema/'),
};

// Tournament related API calls
export const tournamentApi = {
  getAll: (params?: PaginationParams) => api.get('/cricket/series/', { params }),
  getById: (id: number) => api.get(`/cricket/series/${id}/`),
  create: (data: any) => api.post('/cricket/series/', data),
  update: (id: number, data: any) => api.put(`/cricket/series/${id}/`, data),
  patch: (id: number, data: any) => api.patch(`/cricket/series/${id}/`, data),
  delete: (id: number) => api.delete(`/cricket/series/${id}/`),
};

// MatchPlayer related API calls
export const matchPlayerApi = {
  getAll: (params?: PaginationParams) => api.get('/cricket/match_players/', { params }),
  getById: (id: number) => api.get(`/cricket/match_players/${id}/`),
  create: (data: any) => api.post('/cricket/match_players/', data),
  update: (id: number, data: any) => api.put(`/cricket/match_players/${id}/`, data),
  patch: (id: number, data: any) => api.patch(`/cricket/match_players/${id}/`, data),
  delete: (id: number) => api.delete(`/cricket/match_players/${id}/`),
  getByMatch: (matchId: number) => api.get('/cricket/match_players/', { params: { match: matchId, page_size: 1000 } }),
};

// SquadPlayer related API calls
export const squadPlayerApi = {
  getAll: (params?: PaginationParams) => api.get('/cricket/squad_players/', { params }),
  getById: (id: number) => api.get(`/cricket/squad_players/${id}/`),
  create: (data: any) => api.post('/cricket/squad_players/', data),
  update: (id: number, data: any) => api.put(`/cricket/squad_players/${id}/`, data),
  patch: (id: number, data: any) => api.patch(`/cricket/squad_players/${id}/`, data),
  delete: (id: number) => api.delete(`/cricket/squad_players/${id}/`),
};

// Ground related API calls
export const groundApi = {
  getAll: (params?: PaginationParams) => api.get('/cricket/grounds/', { params }),
  getById: (id: number) => api.get(`/cricket/grounds/${id}/`),
  create: (data: any) => api.post('/cricket/grounds/', data),
  update: (id: number, data: any) => api.put(`/cricket/grounds/${id}/`, data),
  patch: (id: number, data: any) => api.patch(`/cricket/grounds/${id}/`, data),
  delete: (id: number) => api.delete(`/cricket/grounds/${id}/`),
};

// Team related API calls
export const teamApi = {
  getAll: (params?: PaginationParams) => api.get('/cricket/teams/', { params }),
  getById: (id: number) => api.get(`/cricket/teams/${id}/`),
  create: (data: any) => api.post('/cricket/teams/', data),
  update: (id: number, data: any) => api.put(`/cricket/teams/${id}/`, data),
  patch: (id: number, data: any) => api.patch(`/cricket/teams/${id}/`, data),
  delete: (id: number) => api.delete(`/cricket/teams/${id}/`),
};

// Authentication related API calls
export const authApi = {
  login: (credentials: { username: string; password: string }) =>
    api.post('/auth/login/', credentials),
  logout: () => api.post('/auth/logout/'),
};

// Menu V2 API endpoints
export const getMenus = async (params?: PaginationParams) => {
  const response = await api.get('/app/menu-v2/', { params });
  return response.data;
};

export const getMenu = async (id: number) => {
  const response = await api.get(`/app/menu-v2/${id}/`);
  return response.data;
};

export const getMenuSchema = async () => {
  const response = await api.get('/app/menu-v2/schema/');
  return response.data;
};

export const createMenu = async (menuData: any) => {
  const response = await api.post('/app/menu-v2/', menuData);
  return response.data;
};

export const updateMenu = async (id: number, menuData: any) => {
  const response = await api.put(`/app/menu-v2/${id}/`, menuData);
  return response.data;
};

export const deleteMenu = async (id: number) => {
  const response = await api.delete(`/app/menu-v2/${id}/`);
  return response.data;
};

// Media Folder related API calls
export const mediaFolderApi = {
  getAll: (params?: { parent?: number | null, root?: boolean | null }) => api.get('/app/folders/', { params }),
  getById: (id: number) => api.get(`/app/folders/${id}/`),
  create: (data: { name: string, parent?: { id: number | null } }) => api.post('/app/folders/', data),
  update: (id: number, data: { name: string }) => api.patch(`/app/folders/${id}/`, data),
  delete: (id: number) => api.delete(`/app/folders/${id}/`),
  getContents: (id: number) => api.get(`/app/folders/${id}/contents/`),
  getPath: (id: number) => api.get(`/app/folders/${id}/path/`),
};

// Media File related API calls
export const mediaFileApi = {
  getAll: (params?: { folder?: number | null, type?: string }) => api.get('/app/files/', { params }),
  getById: (id: number) => api.get(`/app/files/${id}/`),
  update: (id: number, data: any) => api.patch(`/app/files/${id}/`, data),
  delete: (id: number) => api.delete(`/app/files/${id}/`),
  bulkDelete: (ids: number[]) => api.post('/app/files/bulk_delete/', { ids }),
  getRootContents: () => api.get('/app/files/?folder=null'),
  upload: (formData: FormData) => {
    return api.post('/app/files/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Media Image related API calls
export const mediaImageApi = {
  getAll: (params?: { folder?: number | null }) => api.get('/app/images/', { params }),
  getById: (id: number) => api.get(`/app/images/${id}/`),
  update: (id: number, data: any) => api.patch(`/app/images/${id}/`, data),
};

// App API endpoints
export const appApi = {
  getAll: async (params?: PaginationParams) => {
    const response = await api.get('/app/apps/', { params });
    return response.data;
  },
  getById: async (id: number) => {
    const response = await api.get(`/app/apps/${id}/`);
    return response.data;
  }
};

// Dashboard related API calls
export const dashboardApi = {
    getStats: () => api.get<DashboardStats>('/stats/dashboard/stats/'),
};

// User related API calls
export const userApi = {
  getUsers: (params = '') => api.get(`/user/users/${params ? '?' + params : ''}`).then(res => res.data),
  getUser: (id: string) => api.get(`/user/users/${id}/`).then(res => res.data),
  deleteUser: (id: string) => api.delete(`/user/users/${id}/`).then(res => res.data),
  getUserClients: (id: string) => api.get(`/user/users/${id}/clients/`).then(res => res.data),
  getNotifications: (id: string) => api.get(`/user/users/${id}/notifications/`).then(res => res.data),
  sendNotification: (data: any) => api.post('/user/connect/send/', data).then(res => res.data),
};

interface ManualAction {
  id: number;
  action_type: string;
  key: string;
  data: string;
  done: boolean;
  cancelled: boolean;
  created_at: string;
  updated_at: string;
}

interface ManualActionResponse {
  results: ManualAction[];
  count: number;
}

export const manualActionApi = {
  getAll: async (params: { 
    page: number; 
    page_size: number; 
    search?: string;
    done?: string;
    cancelled?: string;
    action_type?: string;
  }): Promise<ManualActionResponse> => {
    const response = await api.get('/xtract/manual-actions/', { 
      params: {
        page: params.page,
        page_size: params.page_size,
        search: params.search,
        done: params.done,
        cancelled: params.cancelled,
        action_type: params.action_type
      }
    });
    return response.data;
  },
  getActionTypes: async (): Promise<string[]> => {
    const response = await api.get('/xtract/manual-actions/get_action_types/');
    return response.data;
  },
  get: async (id: number): Promise<ManualAction> => {
    const response = await api.get(`/xtract/manual-actions/${id}/`);
    return response.data;
  },
  update: async (id: number, data: Partial<ManualAction>): Promise<ManualAction> => {
    const response = await api.patch(`/xtract/manual-actions/${id}/`, data);
    return response.data;
  },
};

// MatchPlayerGroupConfiguration related API calls
export const matchPlayerGroupConfigApi = {
  getAll: (params?: PaginationParams) => api.get('/fantasy/match_player_group_config/', { params }),
  getById: (id: number) => api.get(`/fantasy/match_player_group_config/${id}/`),
  create: (data: any) => api.post('/fantasy/match_player_group_config/', data),
  update: (id: number, data: any) => api.put(`/fantasy/match_player_group_config/${id}/`, data),
  patch: (id: number, data: any) => api.patch(`/fantasy/match_player_group_config/${id}/`, data),
  delete: (id: number) => api.delete(`/fantasy/match_player_group_config/${id}/`),
  getByMatch: (matchId: number) => api.get(`/fantasy/match_player_group_config/by_match/`, { params: { match_id: matchId } }),
};

// Sport Events related API calls
export const sportEventApi = {
  getAll: (params?: { 
    platform?: string;
    sport?: string;
    start_time_after?: string;
    start_time_before?: string;
    page_size?: number;
  }) => api.get('/xtract/sport-events/', { params }),
  getById: (id: number) => api.get(`/xtract/sport-events/${id}/`),
  create: (data: any) => api.post('/xtract/sport-events/', data),
  update: (id: number, data: any) => api.put(`/xtract/sport-events/${id}/`, data),
  patch: (id: number, data: any) => api.patch(`/xtract/sport-events/${id}/`, data),
  delete: (id: number) => api.delete(`/xtract/sport-events/${id}/`),
};

export default api;